// The ONE call that loads a practice's WhatsApp messages (GET /wa-onboarding/my-templates).
//
// Both surfaces use this function - the My Account -> WhatsApp "WhatsApp messages" card and the
// Appointment reminders page note - so auth, URL and response checking live in one place
// (cypress/node-tests/whatsapp-messages-wiring.test.js pins that neither surface calls axios itself).
//
// Same transport as the WhatsApp tab's `my-status` call: the main API base URL, the caller's
// Firebase ID token as a Bearer header (via the tab's own getIdToken, which waits out Firebase's
// async init). The endpoint is Admin / Manager / Super Admin only - anyone else gets a 403, which
// callers treat like any other failure.

import axios from 'axios';
import { getIdToken } from '@/pages/MyAccount/components/firebaseIdToken';
import {
  normalizeTemplates, normalizeEditResult, normalizeSetupResult, normalizeSwitchResult, normalizeCustomResult,
} from '@/utils/whatsappMessages';
import { normalizeApprovedMessages } from '@/utils/whatsappApprovedMessages';

export const MY_TEMPLATES_PATH = '/wa-onboarding/my-templates';
export const MY_TEMPLATES_SETUP_PATH = '/wa-onboarding/my-templates/setup';
export const MY_TEMPLATES_ENABLED_PATH = '/wa-onboarding/my-templates/enabled';
export const MY_TEMPLATES_CUSTOM_PATH = '/wa-onboarding/my-templates/custom';
export const APPROVED_MESSAGES_PATH = '/wa-onboarding/approved-messages';

/**
 * The office's approved messages that staff can send from a conversation (GET /wa-onboarding/approved-messages).
 * Unlike the calls above this is open to ANY employee of the site (they send the messages; only an admin or
 * manager writes them). Answers `{ key, title, preview }` per message, where `preview` is the exact sentence the
 * patient would receive. REJECTS on any failure; the reply bar then simply shows no button.
 *
 * @param {string} siteID
 * @param {object} auth  the Firebase auth instance (`this.$store.state.getAuth()`)
 * @returns {Promise<Array<{key: string, title: string, preview: string}>>}
 */
export async function fetchApprovedWhatsAppMessages(siteID, auth) {
  if (!siteID) throw new Error('fetchApprovedWhatsAppMessages: siteID is required');
  const idToken = await getIdToken(auth);
  if (!idToken) throw new Error('fetchApprovedWhatsAppMessages: not signed in');
  const res = await axios.get(APPROVED_MESSAGES_PATH, {
    baseURL: process.env.VUE_APP_TEAMIO_API_URL,
    params: { siteID },
    headers: { Authorization: `Bearer ${idToken}` },
  });
  return normalizeApprovedMessages(res.data);
}

/**
 * @param {string} siteID the site whose messages to load
 * @param {object} auth   the Firebase auth instance (`this.$store.state.getAuth()`)
 * @param {{ all?: boolean }} [opts] `all` = the messages TABLE: every live message with its on/off switch, plus the
 *        standard messages the office has not set up. Without it the server answers only what is live and switched
 *        on - what the Appointment reminders note needs.
 * @returns {Promise<Array>} the normalized templates (see normalizeTemplates); REJECTS on any failure
 */
export async function fetchWhatsAppMessages(siteID, auth, { all = false } = {}) {
  if (!siteID) throw new Error('fetchWhatsAppMessages: siteID is required');
  const idToken = await getIdToken(auth);
  // No token means no session. Sending anyway would only earn a 401, and a 401 on an authenticated
  // endpoint makes the stale-bundle interceptor run a version check (and possibly reload the tab).
  if (!idToken) throw new Error('fetchWhatsAppMessages: not signed in');
  const res = await axios.get(MY_TEMPLATES_PATH, {
    baseURL: process.env.VUE_APP_TEAMIO_API_URL,
    params: all ? { siteID, view: 'all' } : { siteID },
    headers: { Authorization: `Bearer ${idToken}` },
  });
  return normalizeTemplates(res.data);
}

/**
 * Set up one of the standard messages the office does not have (POST /wa-onboarding/my-templates/setup). It is
 * sent to WhatsApp for approval; nothing a patient receives changes until it is approved. A refusal comes back as
 * a rejection carrying `response.data = { code, message }` (utils `actionErrorMessage` turns it into a sentence).
 *
 * @returns {Promise<{ status: 'pending' }>}
 */
export async function setupWhatsAppMessage(siteID, key, auth) {
  if (!siteID) throw new Error('setupWhatsAppMessage: siteID is required');
  if (!key) throw new Error('setupWhatsAppMessage: key is required');
  const idToken = await getIdToken(auth);
  if (!idToken) throw new Error('setupWhatsAppMessage: not signed in');
  const res = await axios.post(MY_TEMPLATES_SETUP_PATH, { siteID, key }, {
    baseURL: process.env.VUE_APP_TEAMIO_API_URL,
    headers: { Authorization: `Bearer ${idToken}` },
  });
  return normalizeSetupResult(res.data);
}

/**
 * "Send on WhatsApp": switch one message on or off (POST /wa-onboarding/my-templates/enabled). Off means that
 * message goes out as a text again; the approved WhatsApp message is kept. `enabled` must be a real boolean.
 *
 * @returns {Promise<{ key: string, enabled: boolean }>}
 */
export async function setWhatsAppMessageEnabled(siteID, key, enabled, auth) {
  if (!siteID) throw new Error('setWhatsAppMessageEnabled: siteID is required');
  if (!key) throw new Error('setWhatsAppMessageEnabled: key is required');
  if (typeof enabled !== 'boolean') throw new Error('setWhatsAppMessageEnabled: enabled must be true or false');
  const idToken = await getIdToken(auth);
  if (!idToken) throw new Error('setWhatsAppMessageEnabled: not signed in');
  const res = await axios.post(MY_TEMPLATES_ENABLED_PATH, { siteID, key, enabled }, {
    baseURL: process.env.VUE_APP_TEAMIO_API_URL,
    headers: { Authorization: `Bearer ${idToken}` },
  });
  return normalizeSwitchResult(res.data);
}

/**
 * "Add new message": a message the office writes itself, sent to WhatsApp for approval (POST
 * /wa-onboarding/my-templates/custom). Staff send it by hand from a conversation once it is approved; nothing
 * is ever sent automatically. A refusal (name, wording, the limit of 10) comes back as a rejection carrying
 * `response.data = { code, message }` - utils `customErrorMessage` turns it into a sentence.
 *
 * @param {string} siteID
 * @param {string} name  what the office calls it, e.g. 'Late fee reminder'
 * @param {string} body  the text, blanks as [Practice name] / [Office phone]
 * @param {object} auth  the Firebase auth instance
 * @returns {Promise<{ key: string, status: 'pending', warnings: Array<{code, message}> }>}
 */
export async function addWhatsAppCustomMessage(siteID, name, body, auth) {
  if (!siteID) throw new Error('addWhatsAppCustomMessage: siteID is required');
  const idToken = await getIdToken(auth);
  if (!idToken) throw new Error('addWhatsAppCustomMessage: not signed in');
  const res = await axios.post(MY_TEMPLATES_CUSTOM_PATH, { siteID, name, body }, {
    baseURL: process.env.VUE_APP_TEAMIO_API_URL,
    headers: { Authorization: `Bearer ${idToken}` },
  });
  return normalizeCustomResult(res.data);
}

/**
 * Submit an EDITED message for WhatsApp's approval (POST /wa-onboarding/my-templates). The live message
 * keeps sending until the edit is approved; the server owns every rule (this build's validator is only
 * instant feedback), so a refusal comes back as a rejection carrying `response.data = { code, message }`
 * - the card turns it into a sentence with utils `editErrorMessage`.
 *
 * @param {string} siteID
 * @param {string} key   which message, e.g. 'apt-reminder'
 * @param {string} body  what the practice typed, blanks as [Practice name] etc.
 * @param {object} auth  the Firebase auth instance
 * @returns {Promise<{ status: 'pending', warnings: Array<{code, message}> }>}
 */
export async function submitWhatsAppEdit(siteID, key, body, auth) {
  if (!siteID) throw new Error('submitWhatsAppEdit: siteID is required');
  if (!key) throw new Error('submitWhatsAppEdit: key is required');
  const idToken = await getIdToken(auth);
  if (!idToken) throw new Error('submitWhatsAppEdit: not signed in');
  const res = await axios.post(MY_TEMPLATES_PATH, { siteID, key, body }, {
    baseURL: process.env.VUE_APP_TEAMIO_API_URL,
    headers: { Authorization: `Bearer ${idToken}` },
  });
  return normalizeEditResult(res.data);
}
